import React, { useState, useEffect } from 'react';
import { FileText, Plus, Edit, Trash2, CheckCircle } from 'lucide-react';
import { termService } from '../services/termService';
import { Term, TermType, CreateTermRequest } from '../interfaces/Term';
import styles from './Terms.module.css';

const TYPE_LABELS: Record<TermType, string> = {
  platform: 'Plataforma',
  bus: 'Bus',
  aereo: 'Aéreo',
};

const Terms: React.FC = () => {
  const [terms, setTerms] = useState<Term[]>([]);
  const [loading, setLoading] = useState(true);
  const [filterType, setFilterType] = useState<TermType | ''>('');
  const [showModal, setShowModal] = useState(false);
  const [editingTerm, setEditingTerm] = useState<Term | null>(null);
  const [formData, setFormData] = useState<CreateTermRequest>({
    type: 'platform',
    title: '',
    content: '',
  });

  useEffect(() => {
    fetchTerms();
  }, [filterType]);

  const fetchTerms = async () => {
    setLoading(true);
    try {
      const data = await termService.getAll(filterType || undefined);
      setTerms(data);
    } catch (error) {
      alert('Erro ao carregar termos');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = () => {
    setEditingTerm(null);
    setFormData({ type: 'platform', title: '', content: '' });
    setShowModal(true);
  };

  const handleEdit = (term: Term) => {
    setEditingTerm(term);
    setFormData({ type: term.type, title: term.title, content: term.content });
    setShowModal(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (editingTerm) {
        await termService.update(editingTerm.id, {
          title: formData.title,
          content: formData.content,
        });
      } else {
        await termService.create(formData);
      }
      setShowModal(false);
      fetchTerms();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao salvar termo');
    }
  };

  const handleActivate = async (term: Term) => {
    if (!window.confirm(`Ativar "${term.title}" v${term.version}? Isso desativará a versão anterior de "${TYPE_LABELS[term.type]}".`)) return;
    try {
      await termService.activate(term.id);
      fetchTerms();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao ativar termo');
    }
  };

  const handleDelete = async (term: Term) => {
    if (!window.confirm(`Excluir "${term.title}" v${term.version}? Esta ação não pode ser desfeita.`)) return;
    try {
      await termService.delete(term.id);
      fetchTerms();
    } catch (error: any) {
      alert(error.response?.data?.message || 'Erro ao excluir termo');
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.header}>
        <div className={styles.titleRow}>
          <FileText size={24} />
          <h1>Termos de Uso</h1>
        </div>
        <button className={styles.createButton} onClick={handleCreate}>
          <Plus size={16} /> Novo Termo
        </button>
      </div>

      <div className={styles.filters}>
        <button className={`${styles.filterBtn} ${filterType === '' ? styles.active : ''}`} onClick={() => setFilterType('')}>Todos</button>
        <button className={`${styles.filterBtn} ${filterType === 'platform' ? styles.active : ''}`} onClick={() => setFilterType('platform')}>Plataforma</button>
        <button className={`${styles.filterBtn} ${filterType === 'bus' ? styles.active : ''}`} onClick={() => setFilterType('bus')}>Bus</button>
        <button className={`${styles.filterBtn} ${filterType === 'aereo' ? styles.active : ''}`} onClick={() => setFilterType('aereo')}>Aéreo</button>
      </div>

      {loading ? (
        <p className={styles.loading}>Carregando...</p>
      ) : terms.length === 0 ? (
        <p className={styles.empty}>Nenhum termo cadastrado.</p>
      ) : (
        <table className={styles.table}>
          <thead>
            <tr>
              <th>Tipo</th>
              <th>Título</th>
              <th>Versão</th>
              <th>Status</th>
              <th>Criado em</th>
              <th>Ações</th>
            </tr>
          </thead>
          <tbody>
            {terms.map((term) => (
              <tr key={term.id}>
                <td>{TYPE_LABELS[term.type]}</td>
                <td>{term.title}</td>
                <td>v{term.version}</td>
                <td>
                  <span className={term.active ? styles.badgeActive : styles.badgeInactive}>
                    {term.active ? 'Ativo' : 'Inativo'}
                  </span>
                </td>
                <td>{new Date(term.created_at).toLocaleDateString('pt-BR')}</td>
                <td className={styles.actions}>
                  {!term.active && (
                    <>
                      <button className={styles.activateBtn} onClick={() => handleActivate(term)} title="Ativar">
                        <CheckCircle size={16} />
                      </button>
                      <button className={styles.editBtn} onClick={() => handleEdit(term)} title="Editar">
                        <Edit size={16} />
                      </button>
                      <button className={styles.deleteBtn} onClick={() => handleDelete(term)} title="Excluir">
                        <Trash2 size={16} />
                      </button>
                    </>
                  )}
                  {term.active && <span className={styles.activeLabel}>Versão vigente</span>}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      )}

      {showModal && (
        <div className={styles.overlay} onClick={() => setShowModal(false)}>
          <div className={styles.modal} onClick={(e) => e.stopPropagation()}>
            <h2>{editingTerm ? 'Editar Termo' : 'Novo Termo'}</h2>
            <form onSubmit={handleSubmit}>
              <div className={styles.formGroup}>
                <label>Tipo</label>
                <select
                  value={formData.type}
                  onChange={(e) => setFormData({ ...formData, type: e.target.value as TermType })}
                  disabled={!!editingTerm}
                >
                  <option value="platform">Plataforma</option>
                  <option value="bus">Bus</option>
                  <option value="aereo">Aéreo</option>
                </select>
              </div>
              <div className={styles.formGroup}>
                <label>Título</label>
                <input
                  type="text"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  placeholder="Ex: Termos de Compra - Bus v2"
                  required
                />
              </div>
              <div className={styles.formGroup}>
                <label>Conteúdo (HTML)</label>
                <textarea
                  value={formData.content}
                  onChange={(e) => setFormData({ ...formData, content: e.target.value })}
                  placeholder="Cole aqui o conteúdo HTML dos termos..."
                  rows={15}
                  required
                />
              </div>
              <div className={styles.formActions}>
                <button type="button" className={styles.cancelBtn} onClick={() => setShowModal(false)}>Cancelar</button>
                <button type="submit" className={styles.submitBtn}>
                  {editingTerm ? 'Salvar' : 'Criar Termo'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default Terms;
